import React, { useEffect, useRef, useState } from "react";
import {
    PlusCircle, Edit, Trash2, Eye, ChevronDown, ChevronUp, ChevronLeft,
    ChevronRight, Brain, Upload, Search, X, Save, Plus, Loader2,
    FileText, BookOpen, Sparkles, Wand2, CheckCircle2, AlertCircle, Layers,
    Layout, Settings, ArrowRight, Download, Filter, MoreVertical,
    Target, Hash, Clock, Check, HelpCircle
} from "lucide-react";
import axiosInstance from "@/axios";
import Modal from "@/pages/Other/Modal";
import { toast } from "react-toastify";

// Loading component
const Loading = () => (
  <div className="flex flex-col items-center justify-center py-12 space-y-4">
    <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
    <p className="text-slate-500 font-bold animate-pulse">Processing...</p>
  </div>
);

// AI Assistant Modal Component
const AIAssistantModal = ({ isOpen, onClose, courseId, courseName, onQuestionsGenerated }) => {
  const [prompt, setPrompt] = useState('');
  const [course, setCourse] = useState('');
  const [numberOfQuestions, setNumberOfQuestions] = useState(5);
  const [questionType, setQuestionType] = useState('multiple_choice');
  const [loading, setLoading] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [template, setTemplate] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/templates/getAll');
      setTemplate(data.data || []);
    } catch (e) {
      console.error(e.message);
    } finally { setLoading(false); }
  }
  
  useEffect(() => {
    if (courseName) {
      setCourse(courseName);
    }
  }, [courseName]);

  const handleGenerate = async () => {
    if (!course.trim()) {
      toast.warning('Please enter a topic or prompt');
      return;
    }
    setLoading(true);
    try {
      if (!prompt && template.length > 0) {
        toast.info('Using default generation template');
      }
      
      const response = await axiosInstance.post('/quiz/generate-questions', {
        courseId,
        course,
        prompt,
        numberOfQuestions,
        questionType
      });

      if (response.data && response.data.questions) {
        setGeneratedQuestions(response?.data?.questions);
        toast.success('Questions generated successfully! ✨');
      } else {
        toast.error('No questions returned from server');
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      toast.error('Failed to generate questions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseQuestions = () => {
    onQuestionsGenerated(generatedQuestions);
    onClose();
  };

  const handleSelect = (id) => {
    const templates = template.find(t => t.id === parseInt(id));
    setSelectedTemplate(templates);
    setPrompt(templates ? templates.prompt : "");
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;

    try {
      const payload = {
        templateName: selectedTemplate.templateName,
        templateFrom: selectedTemplate.templateFrom,
        prompt: prompt
      }
      const { data } = await axiosInstance.post(`/templates/update/${selectedTemplate.id}`, payload);
      setTemplate(prev =>
        prev.map(t => t.id === selectedTemplate.id ? data.data : t)
      );
      toast.success("Template updated successfully!");
    } catch (err) {
      console.error("❌ Error saving template:", err);
      toast.error("Failed to save template");
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 p-6 bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-500 rounded-3xl text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 blur-2xl"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 backdrop-blur-md rounded-xl">
                    <Brain className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-bold tracking-tight">AI Quiz Engineer</h2>
            </div>
            <p className="text-indigo-100 font-medium">Transform any topic into a professional assessment in seconds.</p>
          </div>
        </div>

        <div className="space-y-8 px-2">
          {/* Input Section */}
          <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-8">
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
                  1. Define the Topic or Source Material
                </label>
                <textarea
                  value={course}
                  onChange={(e) => setCourse(e.target.value)}
                  className="w-full px-5 py-4 border-2 border-slate-100 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/5 outline-none transition-all font-medium text-slate-600"
                  placeholder="e.g., Explain React Hooks, Database Normalization principles, or paste a paragraph from your lecture notes..."
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
                    2. Quantity
                  </label>
                  <select
                    value={numberOfQuestions}
                    onChange={(e) => setNumberOfQuestions(parseInt(e.target.value))}
                    className="w-full px-5 py-3 border-2 border-slate-100 rounded-2xl focus:border-blue-500 outline-none transition-all font-bold text-slate-600 appearance-none bg-slate-50"
                  >
                    <option value={3}>3 Questions</option>
                    <option value={5}>5 Questions</option>
                    <option value={10}>10 Questions</option>
                    <option value={15}>15 Questions</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
                    3. AI Template (Optional)
                  </label>
                  <select
                    onChange={(e) => handleSelect(e.target.value)}
                    className="w-full px-5 py-3 border-2 border-slate-100 rounded-2xl focus:border-blue-500 outline-none transition-all font-bold text-slate-600 appearance-none bg-slate-50"
                  >
                    <option value="">-- Standard Model --</option>
                    {template.map((t) => (
                      <option key={t.id} value={t.id}>{t.templateName}</option>
                    ))}
                  </select>
                </div>
              </div>

              {selectedTemplate && (
                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                  <label className="block text-sm font-bold text-slate-700 uppercase tracking-wider mb-3">
                    Refine AI Prompt
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    rows={4}
                    className="w-full px-5 py-4 border-2 border-slate-100 rounded-2xl focus:border-blue-500 outline-none transition-all font-medium text-slate-500 bg-slate-50/50 italic"
                  />
                  <div className="flex justify-end mt-3">
                    <button
                      onClick={handleSave}
                      className="text-xs font-bold text-blue-600 uppercase tracking-widest hover:text-blue-800 flex items-center gap-1"
                    >
                      <Save size={14} /> Update Template
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleGenerate}
              disabled={loading || !course.trim()}
              className="w-full bg-[#264b82] text-white py-5 rounded-[1.5rem] mt-8 font-bold text-lg shadow-xl shadow-blue-900/20 hover:shadow-blue-900/30 transition-all disabled:opacity-50 flex items-center justify-center gap-3 active:scale-95"
            >
              {loading ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Engineering Questions...
                </>
              ) : (
                <>
                  <Sparkles className="w-6 h-6" />
                  Generate Magic Quiz
                </>
              )}
            </button>
          </div>

          {/* Generated Questions Preview */}
          {generatedQuestions && generatedQuestions.questions && (
            <div className="bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200 p-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-slate-800 tracking-tight">Review Generated Content</h3>
                  <p className="text-slate-500 text-sm font-medium">Verify the AI-generated questions before importing</p>
                </div>
                <span className="bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">
                  {generatedQuestions.questions.length} Items
                </span>
              </div>
              
              <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                {generatedQuestions.questions.map((question, index) => (
                  <div key={index} className="bg-white border border-slate-100 p-6 rounded-2xl shadow-sm">
                    <div className="flex gap-4 mb-4">
                      <span className="flex-shrink-0 w-8 h-8 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center font-bold text-xs">
                        #{index + 1}
                      </span>
                      <p className="font-bold text-slate-800 leading-relaxed pt-1">{question?.question}</p>
                    </div>
                    
                    {question?.options && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-12">
                        {question?.options.map((option, optIndex) => (
                          <div
                            key={optIndex}
                            className={`px-4 py-2.5 rounded-xl text-sm font-bold border ${option?.isCorrect
                              ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                              : 'bg-slate-50 border-slate-100 text-slate-500'
                              }`}
                          >
                            <span className="opacity-50 mr-2">{String.fromCharCode(65 + optIndex)}.</span> {option?.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-4 mt-10">
                <button
                  onClick={() => setGeneratedQuestions([])}
                  className="px-8 py-3.5 border-2 border-slate-200 text-slate-600 rounded-2xl font-bold hover:bg-white transition-all active:scale-95"
                >
                  Discard
                </button>
                <button
                  onClick={handleUseQuestions}
                  className="px-10 py-3.5 bg-emerald-600 text-white rounded-2xl font-bold shadow-lg shadow-emerald-900/20 hover:bg-emerald-700 transition-all flex items-center gap-2 active:scale-95"
                >
                  <Plus className="w-5 h-5" />
                  Import to Quiz
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-center mt-10">
          <button
            onClick={onClose}
            className="text-slate-400 font-bold hover:text-slate-600 flex items-center gap-2 transition-colors"
          >
            <X size={20} /> Dismiss Assistant
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ─── Import Modal ────────────────────────────────────────────────────────────
const ImportModal = ({ isOpen, onClose, courseId, courseName, onImportSuccess }) => {
  const [selectedFormat, setSelectedFormat] = useState('json');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showSamplePopup, setShowSamplePopup] = useState(false);
  const fileInputRef = useRef(null);

  const formats = [
    { id: 'json',  label: 'JSON',  ext: '.json',       accept: '.json' },
    { id: 'csv',   label: 'CSV',   ext: '.csv',        accept: '.csv' },
    { id: 'excel', label: 'Excel', ext: '.xlsx / .xls', accept: '.xlsx,.xls' },
  ];

  const instructions = {
    json: {
      title: 'JSON File Structure',
      example: `{
  "quizName": "My Quiz Title",
  "guidelines": "Read carefully before answering.",
  "questions": [
    {
      "question": "What is the capital of France?",
      "type": "1",
      "answers": [
        { "answer": "London", "isRight": false },
        { "answer": "Paris",  "isRight": true  },
        { "answer": "Berlin", "isRight": false },
        { "answer": "Madrid", "isRight": false }
      ]
    }
  ]
}`,
      rules: [
        '"type": "1" → Single choice  |  "3" → Multiple choice  |  "2" → Fill in the blank',
        'Set "isRight": true for the correct answer(s). Multiple can be true for type "3".',
        'Each question needs at least 2 answers and a maximum of 5.',
        'quizName and guidelines are required at the top level.',
      ],
    },
    csv: {
      title: 'CSV File Structure',
      example: `quizName,guidelines,question,questionType,answer,isRight
My Quiz,Read carefully,Capital of France?,1,London,false
My Quiz,Read carefully,Capital of France?,1,Paris,true
My Quiz,Read carefully,Capital of France?,1,Berlin,false
My Quiz,Read carefully,Capital of France?,1,Madrid,false
My Quiz,Read carefully,Is water H2O?,1,Yes,true
My Quiz,Read carefully,Is water H2O?,1,No,false`,
      rules: [
        'Row 1 must be the exact header: quizName,guidelines,question,questionType,answer,isRight',
        'Each answer option gets its own row — repeat quizName, guidelines, and question per row.',
        'questionType: 1=Single choice, 3=Multiple choice, 2=Fill in the blank',
        'isRight: use lowercase true or false (no quotes in the actual file).',
      ],
    },
    excel: {
      title: 'Excel File Structure (.xlsx / .xls)',
      example: `Row 1 — Headers (exact column names required):
quizName | guidelines | question | questionType | answer | isRight

Row 2+ — Data (one answer option per row):
My Quiz | Read carefully | Capital of France? | 1 | London | FALSE
My Quiz | Read carefully | Capital of France? | 1 | Paris   | TRUE
My Quiz | Read carefully | Capital of France? | 1 | Berlin  | FALSE
My Quiz | Read carefully | Is water H2O?      | 1 | Yes     | TRUE
My Quiz | Read carefully | Is water H2O?      | 1 | No      | FALSE`,
      rules: [
        'Row 1 must contain the exact column headers shown above (case-sensitive).',
        'Each answer option is a separate row — repeat question text for each option.',
        'questionType: 1=Single choice, 3=Multiple choice, 2=Fill in the blank',
        'isRight: use Excel TRUE/FALSE booleans or plain text "true"/"false".',
      ],
    },
  };

  const current = instructions[selectedFormat];
  const currentAccept = formats.find(f => f.id === selectedFormat)?.accept || '.json';

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const handleUpload = async () => {
    if (!file) { toast.warning('Please select a file first'); return; }
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axiosInstance.post(`/course/${courseId}/import-quizzes`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('Import response:', response.data);

      if (response.data && response.data.error) {
        toast.error(response.data.message || 'Import failed. Please check your file structure and try again.');
        return;
      }
      toast.success('Questions imported successfully! 📥');
      onImportSuccess();
      onClose();
      setFile(null);
    } catch (error) {
      const msg = error?.response?.data?.message || 'Import failed. Please check your file structure and try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 p-6 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-500 rounded-3xl text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 blur-2xl" />
          <div className="relative z-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 backdrop-blur-md rounded-xl">
                <Upload className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold tracking-tight">Import Questions</h2>
                <p className="text-emerald-100 font-medium text-sm mt-0.5">
                  Course: <span className="font-bold text-white">{courseName}</span>
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowSamplePopup(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md hover:bg-white/35 active:scale-95 text-white font-bold rounded-xl transition-all border border-white/10 text-sm shadow-md self-start sm:self-auto"
            >
              <HelpCircle size={16} />
              <span>Demo Sample</span>
            </button>
          </div>
        </div>

        <div className="space-y-6 px-2">
          {/* Step 1 – Format selection */}
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">
              Step 1 — Choose File Format
            </label>
            <div className="grid grid-cols-3 gap-3">
              {formats.map(fmt => (
                <button
                  key={fmt.id}
                  type="button"
                  onClick={() => { setSelectedFormat(fmt.id); setFile(null); }}
                  className={`py-4 px-4 rounded-2xl font-bold text-sm transition-all border-2 flex flex-col items-center gap-2 ${
                    selectedFormat === fmt.id
                      ? 'bg-[#264b82] border-[#264b82] text-white shadow-lg shadow-blue-900/20'
                      : 'bg-slate-50 border-slate-50 text-slate-500 hover:border-slate-200'
                  }`}
                >
                  <FileText size={22} />
                  <span>{fmt.label}</span>
                  <span className="text-xs opacity-70">{fmt.ext}</span>
                </button>
              ))}
            </div>
          </div>


          {/* Step 2 – Drop zone */}
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">
              Step 2 — Upload Your File
            </label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-4 border-dashed rounded-2xl p-10 text-center cursor-pointer transition-all ${
                dragOver
                  ? 'border-blue-400 bg-blue-50'
                  : file
                  ? 'border-emerald-400 bg-emerald-50'
                  : 'border-slate-200 hover:border-blue-400 hover:bg-slate-50'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={currentAccept}
                onChange={(e) => setFile(e.target.files[0] || null)}
                className="hidden"
              />
              {file ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center">
                    <CheckCircle2 size={28} className="text-emerald-500" />
                  </div>
                  <p className="font-bold text-emerald-700">{file.name}</p>
                  <p className="text-sm text-slate-400">{(file.size / 1024).toFixed(1)} KB</p>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    className="text-xs text-rose-500 font-bold mt-1 hover:underline"
                  >
                    Remove file
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center">
                    <Upload size={28} className="text-slate-300" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-600">
                      Drop your {selectedFormat.toUpperCase()} file here
                    </p>
                    <p className="text-sm text-slate-400 mt-1">or click to browse</p>
                  </div>
                  <span className="text-xs bg-slate-100 text-slate-400 px-3 py-1 rounded-full font-bold">
                    {formats.find(f => f.id === selectedFormat)?.ext}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 bg-slate-50 text-slate-600 rounded-2xl font-bold hover:bg-slate-100 transition-all active:scale-95"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUpload}
              disabled={loading || !file}
              className="px-10 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl font-bold shadow-xl shadow-emerald-900/20 hover:shadow-emerald-900/30 transition-all flex items-center gap-3 active:scale-95 disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={20} />}
              {loading ? 'Importing…' : 'Import Questions'}
            </button>
          </div>
        </div>
      </div>

      {/* Floating Centered Modal Popup Box for File Structure Reference */}
      {showSamplePopup && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[1100] flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setShowSamplePopup(false)}
        >
          <div
            className="bg-slate-800 rounded-3xl p-6 text-white max-w-2xl w-full shadow-2xl relative border border-slate-700 animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShowSamplePopup(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white bg-slate-700 hover:bg-slate-600 p-2.5 rounded-xl transition-all"
            >
              <X size={18} />
            </button>
            <div className="flex items-center gap-2 mb-4">
              <HelpCircle size={20} className="text-emerald-400" />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                File Structure Reference
              </span>
            </div>
            <h3 className="font-bold text-xl mb-4 text-emerald-400">{current.title}</h3>
            <pre className="bg-slate-900 rounded-2xl p-4 text-xs text-emerald-300 overflow-x-auto font-mono leading-relaxed mb-5 whitespace-pre-wrap max-h-72 custom-scrollbar">
              {current.example}
            </pre>
            <div className="space-y-2.5 max-h-48 overflow-y-auto pr-1 custom-scrollbar">
              {current.rules.map((rule, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 bg-emerald-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 size={12} className="text-emerald-400" />
                  </div>
                  <p className="text-slate-300 text-sm font-medium">{rule}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => setShowSamplePopup(false)}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg transition-all active:scale-95 text-sm"
              >
                Got it, thanks!
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ─── Export Modal ─────────────────────────────────────────────────────────────
const ExportModal = ({ isOpen, onClose, quiz, course }) => {
  const [selectedFormat, setSelectedFormat] = useState('json');
  const label = quiz ? quiz.quizName : course?.courseName;
  const isCourseLevelExport = !quiz && !!course;

  const formats = [
    { id: 'json',  label: 'JSON',  ext: '.json', desc: 'Structured data — best for re-importing' },
    { id: 'csv',   label: 'CSV',   ext: '.csv',  desc: 'Spreadsheet-compatible, easy to edit' },
    { id: 'excel', label: 'Excel', ext: '.xlsx', desc: 'Open directly in Microsoft Excel' },
  ];

  const buildRows = () => {
    const rows = [['quizName', 'guidelines', 'question', 'questionType', 'answer', 'isRight']];
    const quizzes = quiz ? [quiz] : (course?.quizzes || []);
    quizzes.forEach(qz => {
      (qz.questions || []).forEach(q => {
        (q.options || []).forEach(opt => {
          rows.push([
            `"${(qz.quizName || '').replace(/"/g, '""')}"`,
            `"${(qz.quizGuidelines || '').replace(/"/g, '""')}"`,
            `"${(q.question || '').replace(/"/g, '""')}"`,
            q.type || '1',
            `"${(opt.answer || '').replace(/"/g, '""')}"`,
            opt.isRight ? 'true' : 'false',
          ]);
        });
      });
    });
    return rows.map(r => r.join(',')).join('\n');
  };

  const buildJSON = () => {
    if (quiz) {
      return {
        quizName: quiz.quizName,
        guidelines: quiz.quizGuidelines || '',
        questions: (quiz.questions || []).map(q => ({
          question: q.question,
          type: q.type || '1',
          answers: (q.options || []).map(opt => ({ answer: opt.answer, isRight: opt.isRight })),
        })),
      };
    }
    return (course?.quizzes || []).map(qz => ({
      quizName: qz.quizName,
      guidelines: qz.quizGuidelines || '',
      questions: (qz.questions || []).map(q => ({
        question: q.question,
        type: q.type || '1',
        answers: (q.options || []).map(opt => ({ answer: opt.answer, isRight: opt.isRight })),
      })),
    }));
  };

  const triggerDownload = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExport = () => {
    const safeName = (label || 'quiz').replace(/[^a-z0-9]/gi, '_');
    if (selectedFormat === 'json') {
      triggerDownload(JSON.stringify(buildJSON(), null, 2), `${safeName}.json`, 'application/json');
    } else if (selectedFormat === 'csv') {
      triggerDownload(buildRows(), `${safeName}.csv`, 'text/csv');
    } else {
      triggerDownload(buildRows(), `${safeName}.xlsx`, 'text/csv');
    }
    toast.success(`Exported as ${selectedFormat.toUpperCase()} successfully! 📤`);
    onClose();
  };

  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="mb-8 p-6 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-3xl text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10 blur-2xl" />
          <div className="relative z-10 flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-md rounded-xl">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Export Quiz</h2>
              <p className="text-purple-100 font-medium text-sm mt-0.5 line-clamp-1">{label}</p>
            </div>
          </div>
        </div>

        <div className="space-y-6 px-2">
          {isCourseLevelExport && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex gap-3">
              <AlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-amber-700">
                This will export all{' '}
                <span className="font-bold">{course?.quizzes?.length}</span>{' '}
                quiz(zes) from this course in a single file.
              </p>
            </div>
          )}

          {/* Format selection */}
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-3">
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">
              Choose Export Format
            </label>
            {formats.map(fmt => (
              <button
                key={fmt.id}
                type="button"
                onClick={() => setSelectedFormat(fmt.id)}
                className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left ${
                  selectedFormat === fmt.id
                    ? 'border-[#264b82] bg-blue-50/50'
                    : 'border-slate-100 hover:border-slate-200'
                }`}
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-[10px] tracking-wide ${
                    selectedFormat === fmt.id
                      ? 'bg-[#264b82] text-white shadow-lg shadow-blue-900/20'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {fmt.ext}
                </div>
                <div className="flex-1">
                  <p className="font-bold text-slate-800">{fmt.label}</p>
                  <p className="text-xs text-slate-400 font-medium">{fmt.desc}</p>
                </div>
                {selectedFormat === fmt.id && (
                  <CheckCircle2 size={20} className="text-[#264b82] flex-shrink-0" />
                )}
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-4 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 bg-slate-50 text-slate-600 rounded-2xl font-bold hover:bg-slate-100 transition-all active:scale-95"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="px-10 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-2xl font-bold shadow-xl shadow-purple-900/20 hover:shadow-purple-900/30 transition-all flex items-center gap-3 active:scale-95"
            >
              <Download size={20} />
              Download {selectedFormat.toUpperCase()}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const QuizModal = ({
  isOpen,
  onClose,
  courseId,
  courseName,
  existingQuiz = null,
  onQuizSaved,
  aiGeneratedQuestions = [],
}) => {

  const [quizCreation, setQuizCreation] = useState({
    id: courseId,
    name: "",
    guidelines: "",
    questions: []
  });
  const [quizUI, setQuizUI] = useState({
    title: true,
    instruction: true,
    multipleChoice: true,
    createQuestionType: [{ quizType: "" }],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quizValidateMsg, setQuizValidateMsg] = useState(false);
  const [questionIncrement, setQuestionIncrement] = useState(0);
  const [addStatus, setAddStatus] = useState(null);
  const [expandedQuiz, setExpandedQuiz] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (aiGeneratedQuestions && Array.isArray(aiGeneratedQuestions.questions)) {
      const mappedQuestions = aiGeneratedQuestions.questions.map((q, idx) => ({
        id: `${idx}`,
        question: q?.question || "",
        type: (q?.type === "multiple_choice" || q?.type === "3") ? "3" : 
              (q?.type === "fill_in_the_blank" || q?.type === "2") ? "2" : 
              "1",
        isNew: true,
        answers: (q?.options?.length ? q.options : [{ text: "", isCorrect: false }])
          .map((opt, id) => ({
            id: `${id}`,
            answer: opt?.answer || opt?.text || opt?.option || "",
            isRight: (opt?.isRight !== undefined) ? (opt.isRight === true || opt.isRight === "true" || opt.isRight === 1 || opt.isRight === "1") :
                     (opt?.isCorrect !== undefined) ? (opt.isCorrect === true || opt.isCorrect === "true" || opt.isCorrect === 1 || opt.isCorrect === "1") :
                     (opt?.is_right !== undefined) ? (opt.is_right === true || opt.is_right === "true" || opt.is_right === 1 || opt.is_right === "1") :
                     (opt?.is_correct !== undefined) ? (opt.is_correct === true || opt.is_correct === "true" || opt.is_correct === 1 || opt.is_correct === "1") :
                     (id === 0)
          }))
      }));

      setQuizCreation({
        id: courseId,
        name: aiGeneratedQuestions.name || "",
        guidelines: aiGeneratedQuestions.guidelines || "",
        questions: mappedQuestions
      });
      setExpandedQuiz(0);
    }
  }, [aiGeneratedQuestions, courseId]);

  const fetchExistingQuizDetails = async () => {
    if (existingQuiz && existingQuiz.quizId) {
      try {
        setQuizCreation({
          id: courseId,
          name: existingQuiz.quizName || "",
          guidelines: existingQuiz.quizGuidelines || "",
          questions: existingQuiz.questions.map(q => ({
            id: q.questionId,
            question: q.question,
            type: q.type || "1",
            isNew: false,
            answers: (q.options || q.answers || []).map(opt => ({
              id: opt.optionId || opt.id,
              answer: opt.answer || opt.text || "",
              isRight: opt.isRight === true || opt.isRight === "true" || opt.isRight === 1 || opt.isRight === "1"
            }))
          })) || []
        });
      } catch (err) {
        console.error("Error fetching quiz details:", err);
        setError("Failed to load quiz details");
      }
    } else if (!aiGeneratedQuestions || !aiGeneratedQuestions.questions) {
      setQuizCreation({ id: courseId, name: "", guidelines: "", questions: [] });
    }
  };


  useEffect(() => {
    if (isOpen) {
      fetchExistingQuizDetails();
    }
  }, [isOpen, existingQuiz]);

  const inputHandler = (e) => {
    const { name, value } = e.target;
    setQuizCreation(prev => ({ ...prev, [name]: value }));
  };

  const checkInputHandler = (qIndex, index, name, value, accessor) => {
    setQuizCreation(prev => {
      const updatedQuestions = prev.questions.map((q, idx) => {
        if (idx !== qIndex) return q;
        
        let newQuestion = { ...q, [accessor]: value };
        
        // If the question type is changed to Single Choice ("1"), ensure only the first correct answer remains correct.
        if (accessor === "type" && value === "1") {
          let foundRight = false;
          newQuestion.answers = q.answers.map(ans => {
            if (ans.isRight && !foundRight) {
              foundRight = true;
              return ans;
            }
            return { ...ans, isRight: false };
          });
          // If no correct option was found, mark the first one as correct.
          if (!foundRight && newQuestion.answers.length > 0) {
            newQuestion.answers = newQuestion.answers.map((ans, aIdx) => 
              aIdx === 0 ? { ...ans, isRight: true } : ans
            );
          }
        }
        
        return newQuestion;
      });
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleAnswerRightToggle = (qIndex, aIndex) => {
    setQuizCreation(prev => {
      const updatedQuestions = prev.questions.map((q, idx) => {
        if (idx !== qIndex) return q;
        
        const isSingleChoice = q.type === "1";
        const updatedAnswers = q.answers.map((ans, aidx) => {
          if (isSingleChoice) {
            return { ...ans, isRight: aidx === aIndex };
          } else {
            if (aidx === aIndex) {
              return { ...ans, isRight: !ans.isRight };
            }
            return ans;
          }
        });
        
        return { ...q, answers: updatedAnswers, isModified: true };
      });
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleInputChange = (qIndex, aIndex, name, value, accessor, object) => {
    setQuizCreation(prev => {
      const updatedQuestions = prev.questions.map((q, idx) => {
        if (idx !== qIndex) return q;
        
        const updatedAnswers = q.answers.map((ans, aidx) => {
          if (aidx !== aIndex) return ans;
          return { ...ans, [accessor]: value };
        });
        
        return { ...q, answers: updatedAnswers, isModified: true };
      });
      return { ...prev, questions: updatedQuestions };
    });
  };

  const addFormFields = (qIndex, event, property, max, value) => {
    event.preventDefault();
    if (quizCreation.questions[qIndex].answers.length >= max) {
      toast.info(`Maximum ${max} options allowed`);
      return;
    }
    setQuizCreation(prev => {
      const updatedQuestions = prev.questions.map((q, idx) => {
        if (idx !== qIndex) return q;
        return {
          ...q,
          answers: [...q.answers, value],
          isModified: true
        };
      });
      return { ...prev, questions: updatedQuestions };
    });
  };

  const addCreateQuestion = (event) => {
    event.preventDefault();
    const lastQuestion = quizCreation.questions[quizCreation.questions.length - 1];
    
    if (lastQuestion && !lastQuestion.answers.some(item => item.isRight)) {
      setAddStatus("Please mark at least one correct answer for the current question.");
      return;
    }

    setAddStatus(null);
    const newQuestion = {
      question: "",
      type: "1",
      answers: [{ answer: "", isRight: false }, { answer: "", isRight: false }],
      isNew: true
    };

    setQuizCreation(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
    setExpandedQuiz(quizCreation.questions.length);
  };

  const removeFormFields = (qIndex, event, aIndex) => {
    event.preventDefault();
    if (quizCreation.questions[qIndex].answers.length <= 1) return;
    setQuizCreation(prev => {
      const updatedQuestions = prev.questions.map((q, idx) => {
        if (idx !== qIndex) return q;
        return {
          ...q,
          answers: q.answers.filter((_, aidx) => aidx !== aIndex),
          isModified: true
        };
      });
      return { ...prev, questions: updatedQuestions };
    });
  };

  const questionDelete = (qIndex) => {
    if (window.confirm("Delete this question?")) {
      setQuizCreation(prev => ({
        ...prev,
        questions: prev.questions.filter((_, idx) => idx !== qIndex)
      }));
      if (expandedQuiz === qIndex) setExpandedQuiz(null);
    }
  };

  const optionStatus = (isRight, questionIndex, answerIndex, type) => {
    if (type === "1") { // Single Choice
      const thisAnswers = quizCreation.questions[questionIndex].answers;
      const rightAnswerIndex = thisAnswers.findIndex(item => item.isRight);
      if (rightAnswerIndex > -1 && rightAnswerIndex !== answerIndex) {
        return true; // Disable other checkboxes
      }
    }
    return false;
  };

  const handleCreateFirstQuestion = () => {
    if (!quizCreation.name) {
      setQuizValidateMsg(true);
      toast.warning("Please provide a Quiz Title first");
      return;
    }
    setQuizCreation(prev => ({
      ...prev,
      questions: [
        {
          id: `0`,
          question: "",
          type: "1",
          isNew: true,
          answers: [
            { id: "0", answer: "", isRight: false },
            { id: "1", answer: "", isRight: false }
          ]
        }
      ]
    }));
    setExpandedQuiz(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!quizCreation.name) {
      setQuizValidateMsg(true);
      return;
    }

    const invalidQuestions = quizCreation.questions.filter(q =>
      !q.answers.some(a => a.isRight)
    );
    
    if (invalidQuestions.length > 0) {
      setError("Every question must have at least one correct answer.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        id: courseId,
        name: quizCreation.name,
        guidelines: quizCreation.guidelines,
        questions: quizCreation.questions.map(q => ({
          ...(q.id && !q.isNew && { id: q.id }),
          question: q.question,
          type: q.type,
          answers: q.answers.map(a => ({
            ...(a.id && !q.isNew && { id: a.id }),
            answer: a.answer,
            isRight: a.isRight
          }))
        }))
      };

      const endpoint = existingQuiz ? `/quiz/${existingQuiz.quizId}` : "/quiz";
      await axiosInstance.post(endpoint, payload);
      toast.success("Quiz architected successfully! 🏆");
      onQuizSaved();
      onClose();
    } catch (err) {
      console.error("Error saving quiz:", err);
      setError("Architectural failure. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const toggleQuiz = (idx) => setExpandedQuiz(expandedQuiz === idx ? null : idx);

  const filteredQuestions = quizCreation.questions.filter(q =>
    !searchTerm || (q.question && q.question.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8 px-4">
            <div>
                <h2 className="text-3xl font-bold text-slate-800 tracking-tight">{existingQuiz ? "Refine Quiz" : "Architect New Quiz"}</h2>
                <p className="text-slate-500 font-medium">Drafting for: <span className="text-blue-600 font-bold">{courseName}</span></p>
            </div>
            {existingQuiz && (
                 <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Filter questions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm font-bold"
                    />
                 </div>
            )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Metadata Section */}
          <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 md:p-8 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <Layout size={14} className="text-blue-500" /> Quiz Identity
                    </label>
                    <input
                        type="text"
                        name="name"
                        value={quizCreation.name}
                        onChange={inputHandler}
                        className={`w-full px-5 py-4 border-2 rounded-2xl outline-none transition-all font-bold text-lg ${
                            quizValidateMsg && !quizCreation.name ? 'border-rose-200 bg-rose-50' : 'border-slate-50 bg-slate-50 focus:border-blue-500 focus:bg-white'
                        }`}
                        placeholder="Enter an engaging quiz title..."
                        required
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                        <FileText size={14} className="text-amber-500" /> Mission Directives
                    </label>
                    <textarea
                        name="guidelines"
                        value={quizCreation.guidelines}
                        onChange={inputHandler}
                        className="w-full px-5 py-4 border-2 border-slate-50 bg-slate-50 rounded-2xl outline-none transition-all font-medium text-slate-600 focus:border-blue-500 focus:bg-white"
                        rows="1"
                        placeholder="Instructions for the participants..."
                    />
                </div>
            </div>
          </div>

          {/* Question Builder */}
          <div className="space-y-4">
            {filteredQuestions.map((question, qIndex) => {
              const actualIndex = quizCreation.questions.indexOf(question);
              return (
                <div key={actualIndex} className={`bg-white rounded-3xl border-2 transition-all overflow-hidden ${
                    expandedQuiz === actualIndex ? 'border-blue-500 shadow-xl shadow-blue-900/5' : 'border-slate-100 shadow-sm hover:border-slate-200'
                }`}>
                  <div 
                      className="flex justify-between items-center p-6 cursor-pointer"
                      onClick={() => toggleQuiz(actualIndex)}
                  >
                    <div className="flex items-center gap-4">
                      <span className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm transition-colors ${
                          expandedQuiz === actualIndex ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-500'
                      }`}>
                        {actualIndex + 1}
                      </span>
                      <h3 className="font-bold text-slate-800 text-lg line-clamp-1">
                        {question.question || "New Draft Question"}
                      </h3>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); questionDelete(actualIndex); }}
                          className="p-2.5 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                      >
                          <Trash2 size={18} />
                      </button>
                      {expandedQuiz === actualIndex ? <ChevronDown className="text-blue-500" /> : <ChevronUp className="text-blue-600" />}
                    </div>
                  </div>

                  {expandedQuiz === actualIndex && (
                    <div className="p-6 md:p-8 border-t border-slate-50 animate-in slide-in-from-top-2 duration-300">
                      <div className="space-y-8">
                        <div>
                          <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Assessment Query</label>
                          <textarea
                            value={question.question}
                            onChange={({ target }) => checkInputHandler(actualIndex, actualIndex, "question", target.value, "question")}
                            className="w-full px-6 py-4 border-2 border-slate-50 bg-slate-50 rounded-2xl outline-none transition-all font-bold text-slate-800 focus:border-blue-500 focus:bg-white"
                            placeholder="What would you like to ask?"
                            rows="3"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                           <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Choice Mode</label>
                              <div className="flex gap-2">
                                  {[
                                      {id: '1', label: 'Single', icon: <CheckCircle2 size={14} />},
                                      {id: '3', label: 'Multiple', icon: <PlusCircle size={14} />},
                                      {id: '2', label: 'Blanks', icon: <Edit size={14} />}
                                  ].map(type => (
                                      <button
                                          key={type.id}
                                          type="button"
                                          onClick={() => checkInputHandler(actualIndex, actualIndex, "type", type.id, "type")}
                                          className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold text-xs transition-all border-2 ${
                                              question.type === type.id 
                                              ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-900/20' 
                                              : 'bg-slate-50 border-slate-50 text-slate-500 hover:border-slate-200'
                                          }`}
                                      >
                                          {type.icon} {type.label}
                                      </button>
                                  ))}
                              </div>
                           </div>
                           <div>
                              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Answer Inventory</label>
                              <div className="space-y-3">
                                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                                      <Check size={10} className="text-emerald-500" /> Click the check to mark correct answer
                                  </p>
                                  {question.answers.map((answer, aIndex) => (
                                      <div key={aIndex} className="flex items-center gap-2 group">
                                          <div className="relative flex-1">
                                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none select-none">
                                                  {String.fromCharCode(65 + aIndex)}
                                              </span>
                                              <input
                                                  type="text"
                                                  value={answer.answer}
                                                  onChange={({ target }) => handleInputChange(actualIndex, aIndex, "answers", target.value, "answer", "questions")}
                                                  className={`w-full pl-8 pr-4 py-3 rounded-xl border-2 transition-all font-bold text-sm ${
                                                      answer.isRight ? 'border-emerald-500 bg-emerald-50/30' : 'border-slate-100 bg-slate-50 focus:border-blue-500'
                                                  }`}
                                                  placeholder={`Option ${String.fromCharCode(65 + aIndex)}`}
                                              />
                                          </div>
                                          <button
                                              type="button"
                                              title={answer.isRight ? "Correct answer (click to unmark)" : "Mark as correct answer"}
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  e.preventDefault();
                                                  handleAnswerRightToggle(actualIndex, aIndex);
                                              }}
                                              className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all border-2 ${
                                                  answer.isRight
                                                      ? 'bg-emerald-500 text-white border-emerald-500 shadow-lg shadow-emerald-500/30'
                                                      : 'bg-white text-slate-300 border-slate-200 hover:border-emerald-400 hover:text-emerald-400'
                                              }`}
                                          >
                                              <Check size={15} className="stroke-[3]" />
                                          </button>
                                          {question.answers.length > 2 && (
                                              <button
                                                  type="button"
                                                  onClick={(e) => removeFormFields(actualIndex, e, aIndex)}
                                                  className="flex-shrink-0 text-slate-300 hover:text-rose-500 transition-colors"
                                              >
                                                  <X size={18} />
                                              </button>
                                          )}
                                      </div>
                                  ))}
                                  <button
                                      type="button"
                                      onClick={(e) => addFormFields(actualIndex, e, "answers", 5, { answer: "", isRight: false })}
                                      className="w-full py-2 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 font-bold text-xs hover:border-blue-500 hover:text-blue-500 transition-all flex items-center justify-center gap-2"
                                  >
                                      <Plus size={14} /> Add Choice
                                  </button>
                              </div>
                           </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {quizCreation.questions.length === 0 ? (
                <div className="bg-white p-12 rounded-[2.5rem] border-4 border-dashed border-slate-100 text-center">
                    <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
                        <HelpCircle size={40} className="text-blue-200" />
                    </div>
                    <h4 className="text-2xl font-bold text-slate-800 mb-2">No Questions Defined</h4>
                    <p className="text-slate-500 max-w-sm mx-auto font-medium mb-8">Ready to build your assessment? Start by creating your first question manually or use the AI assistant.</p>
                    <button
                        type="button"
                        onClick={handleCreateFirstQuestion}
                        className="bg-[#264b82] text-white px-10 py-4 rounded-2xl font-bold shadow-xl shadow-blue-900/20 hover:scale-105 transition-all flex items-center gap-2 mx-auto"
                    >
                        <Plus size={20} /> Build Question 1
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={addCreateQuestion}
                    className="w-full py-6 border-4 border-dashed border-slate-100 rounded-[2rem] text-slate-300 font-bold text-xl hover:border-blue-500 hover:text-blue-500 hover:bg-blue-50/30 transition-all flex items-center justify-center gap-4 group"
                >
                    <PlusCircle size={32} className="group-hover:rotate-90 transition-transform duration-500" />
                    Add Question {quizCreation.questions.length + 1}
                </button>
            )}
          </div>

          {error && (
            <div className="bg-rose-50 border-2 border-rose-100 text-rose-700 p-6 rounded-[1.5rem] flex items-center gap-4 animate-in shake duration-500">
               <AlertCircle className="flex-shrink-0" />
               <p className="font-bold">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-4 pt-10 border-t border-slate-100 px-4">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-4 bg-slate-50 text-slate-600 rounded-2xl font-bold hover:bg-slate-100 transition-all active:scale-95"
            >
              Discard Changes
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-12 py-4 bg-gradient-to-r from-[#264b82] to-indigo-700 text-white rounded-2xl font-bold shadow-xl shadow-blue-900/30 hover:shadow-blue-900/40 transition-all flex items-center gap-3 active:scale-95 disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" /> : <Save size={20} />}
              {existingQuiz ? "Publish Updates" : "Publish Quiz"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

const QuizDetails = ({ quiz, onClose }) => {
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [inspectPage, setInspectPage] = useState(1);
  const questionsPerPage = 5;

  if (!quiz) return null;

  const totalQuestions = quiz.questions?.length || 0;
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);
  const pagedQuestions = quiz.questions?.slice(
    (inspectPage - 1) * questionsPerPage,
    inspectPage * questionsPerPage
  ) || [];

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const handlePageChange = (page) => {
    setInspectPage(page);
    setExpandedQuestions({});
  };

  return (
    <Modal isOpen={quiz} onClose={onClose}>
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex justify-between items-start mb-8">
            <div className="space-y-2">
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest">Inspection Mode</span>
                <h2 className="text-4xl font-bold text-slate-800 tracking-tight">{quiz.quizName}</h2>
                <div className="flex gap-4">
                    <span className="flex items-center gap-1.5 text-slate-400 text-sm font-bold"><Layers size={14} /> {totalQuestions} Questions</span>
                    <span className="flex items-center gap-1.5 text-slate-400 text-sm font-bold"><Clock size={14} /> Timed Assessment</span>
                </div>
            </div>
            <button onClick={onClose} className="p-3 bg-slate-50 rounded-2xl text-slate-400 hover:text-slate-600 transition-all"><X /></button>
        </div>

        {quiz.quizGuidelines && (
            <div className="bg-amber-50/50 border border-amber-100 p-6 rounded-[2rem] mb-10 flex gap-4">
                <AlertCircle className="text-amber-500 flex-shrink-0" />
                <p className="text-slate-700 font-medium leading-relaxed">{quiz.quizGuidelines}</p>
            </div>
        )}

        <div className="space-y-3">
            {pagedQuestions.map((question, index) => {
                const globalIndex = (inspectPage - 1) * questionsPerPage + index;
                const isExpanded = !!expandedQuestions[question.questionId];
                return (
                    <div
                        key={question.questionId}
                        className="bg-white border border-slate-100 p-4 rounded-2xl shadow-sm transition-all hover:border-slate-200 cursor-pointer"
                        onClick={() => toggleQuestion(question.questionId)}
                    >
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <span className="flex-shrink-0 w-7 h-7 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center font-bold text-xs">
                                    {globalIndex + 1}
                                </span>
                                <h4 className="text-base font-bold text-slate-800 leading-snug">{question.question}</h4>
                            </div>
                            <button
                                type="button"
                                className="p-1.5 hover:bg-slate-50 rounded-lg text-slate-400 transition-all flex-shrink-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleQuestion(question.questionId);
                                }}
                            >
                                <ChevronDown
                                    size={18}
                                    className={`transition-transform duration-300 ${isExpanded ? 'rotate-180 text-blue-600' : ''}`}
                                />
                            </button>
                        </div>

                        {isExpanded && (
                            <div
                                className="mt-3 pt-3 border-t border-slate-100 animate-in fade-in duration-200"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {question.options?.map((option, optIndex) => {
                                        const isRight = !!option.isRight;
                                        return (
                                            <div
                                                key={option.optionId}
                                                className={`flex items-center gap-2.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
                                                    isRight
                                                        ? "bg-emerald-50 text-emerald-800 border-emerald-100"
                                                        : "bg-slate-50 text-slate-600 border-transparent"
                                                }`}
                                            >
                                                <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                                                    isRight ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-500'
                                                }`}>
                                                    {String.fromCharCode(65 + optIndex)}
                                                </span>
                                                <span className="leading-relaxed">{option.answer}</span>
                                                {isRight && <Check className="ml-auto text-emerald-600 flex-shrink-0" size={13} />}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>

        {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
                <button
                    onClick={() => handlePageChange(inspectPage - 1)}
                    disabled={inspectPage === 1}
                    className="p-2 rounded-xl bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <ChevronDown size={16} className="rotate-90" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`w-8 h-8 rounded-xl text-xs font-bold transition-all ${
                            page === inspectPage
                                ? 'bg-[#264b82] text-white shadow-md shadow-blue-900/20'
                                : 'bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-blue-600'
                        }`}
                    >
                        {page}
                    </button>
                ))}
                <button
                    onClick={() => handlePageChange(inspectPage + 1)}
                    disabled={inspectPage === totalPages}
                    className="p-2 rounded-xl bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <ChevronDown size={16} className="-rotate-90" />
                </button>
            </div>
        )}

        <div className="flex justify-between items-center mt-8">
            <span className="text-xs text-slate-400 font-medium">
                Showing {Math.min((inspectPage - 1) * questionsPerPage + 1, totalQuestions)}–{Math.min(inspectPage * questionsPerPage, totalQuestions)} of {totalQuestions} questions
            </span>
            <button
                onClick={onClose}
                className="px-8 py-3.5 bg-[#264b82] text-white rounded-2xl font-bold shadow-xl shadow-blue-900/20 hover:scale-105 transition-all active:scale-95 text-sm"
            >
                Close Inspection
            </button>
        </div>
      </div>
    </Modal>
  );
};

const Quizs = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedCourseName, setSelectedCourseName] = useState('');
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [viewingQuiz, setViewingQuiz] = useState(null);
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [courseSearch, setCourseSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [aiGeneratedQuestions, setAiGeneratedQuestions] = useState([]);
  const [quizFilter, setQuizFilter] = useState("all");
  const [sortOption, setSortOption] = useState("nameAsc");
  const [hiddenQuizzes, setHiddenQuizzes] = useState([]);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [importingCourse, setImportingCourse] = useState({ id: null, name: '' });
  const [exportTarget, setExportTarget] = useState({ quiz: null, course: null });

  const examsPerPage = 7;

  const fetchCoursesWithQuizzes = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/admin/courses-with-quizzes");
      setCourses(response?.data?.data || []);
    } catch (err) {
      console.error("Error fetching courses:", err);
      setError("System failure while syncing assessments.");
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => { fetchCoursesWithQuizzes(); }, []);

  const toggleCourseExpansion = (id) => setExpandedCourseId(expandedCourseId === id ? null : id);

  const openCreateQuizModal = (id, name) => {
    setSelectedCourseId(id);
    setSelectedCourseName(name);
    setSelectedQuiz(null);
    setAiGeneratedQuestions([]);
    setIsModalOpen(true);
  };

  const openAIAssistant = (course) => {
    setSelectedCourseId(course?.courseId);
    setSelectedCourseName(course?.courseName)
    setIsAIModalOpen(true);
  };

  const handleAIQuestionsGenerated = (questions) => {
    setAiGeneratedQuestions(questions);
    setIsAIModalOpen(false);
    setIsModalOpen(true);
  };

  const handleOpenImport = (courseId, courseName) => {
    setImportingCourse({ id: courseId, name: courseName });
    setIsImportModalOpen(true);
  };

  const handleOpenExport = (quiz = null, course = null) => {
    setExportTarget({ quiz, course });
    setIsExportModalOpen(true);
  };

  const openEditQuizModal = (quiz) => {
    setSelectedCourseId(quiz.courseId);
    setSelectedQuiz(quiz);
    setAiGeneratedQuestions([]);
    setIsModalOpen(true);
  };

  const handleDeleteQuiz = async (quizId) => {
    if (window.confirm("Delete this assessment permanently?")) {
      try {
        await axiosInstance.delete(`/quiz/${quizId}`);
        toast.success("Quiz deleted successfully");
        fetchCoursesWithQuizzes();
      } catch (err) {
        toast.error("Failed to delete ❌");
      }
    }
  };

  const filteredCourses = courses
    .filter(c => c.courseName.toLowerCase().includes(courseSearch.toLowerCase()))
    .filter(c => {
      if (quizFilter === "hasQuizzes") return c.quizzes?.length > 0;
      if (quizFilter === "noQuizzes") return !c.quizzes?.length;
      return true;
    })
    .sort((a, b) => {
      if (sortOption === "nameAsc") return a.courseName.localeCompare(b.courseName);
      if (sortOption === "quizzesDesc") return (b.quizzes?.length || 0) - (a.quizzes?.length || 0);
      return 0;
    });

  const paginatedCourses = filteredCourses.slice((currentPage - 1) * examsPerPage, currentPage * examsPerPage);

  return (
    <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div>
                <h1 className="text-3xl font-bold text-slate-800 tracking-tight mb-1">Quiz Architect</h1>
                <p className="text-slate-500 font-medium text-sm">Design and deploy powerful assessments for your students.</p>
            </div>
            <div className="flex items-center gap-3">
                <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-3">
                    <div className="flex flex-col items-center border-r border-slate-100 pr-3">
                        <span className="text-xl font-bold text-blue-600">{courses.length}</span>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Courses</span>
                    </div>
                    <div className="flex flex-col items-center">
                        <span className="text-xl font-bold text-indigo-600">
                            {courses.reduce((acc, curr) => acc + (curr.quizzes?.length || 0), 0)}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Total Quizzes</span>
                    </div>
                </div>
            </div>
        </div>

        {/* Intelligence Bar */}
        <div className="bg-white p-3 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-50 mb-5 flex flex-col lg:flex-row items-center gap-4">
            <div className="relative flex-1 w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 w-5 h-5" />
                <input
                    type="text"
                    placeholder="Find a course..."
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    className="w-full pl-12 pr-6 py-3 bg-slate-50 border-2 border-slate-50 rounded-2xl outline-none focus:border-blue-500 focus:bg-white transition-all font-bold text-slate-600"
                />
            </div>
            <div className="flex items-center gap-4 w-full lg:w-auto">
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100">
                    <Filter size={16} className="text-slate-400" />
                    <select
                        value={quizFilter}
                        onChange={(e) => setQuizFilter(e.target.value)}
                        className="bg-transparent font-bold text-xs text-slate-500 outline-none uppercase tracking-widest"
                    >
                        <option value="all">All Access</option>
                        <option value="hasQuizzes">Has Quizzes</option>
                        <option value="noQuizzes">Empty</option>
                    </select>
                </div>
                <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-2xl border border-slate-100">
                    <Settings size={16} className="text-slate-400" />
                    <select
                        value={sortOption}
                        onChange={(e) => setSortOption(e.target.value)}
                        className="bg-transparent font-bold text-xs text-slate-500 outline-none uppercase tracking-widest"
                    >
                        <option value="nameAsc">A-Z Name</option>
                        <option value="quizzesDesc">High Volume</option>
                    </select>
                </div>
            </div>
        </div>

        {loading ? <Loading /> : (
            <div className="space-y-3">
                {paginatedCourses.map((course) => (
                    <div key={course.courseId} className={`bg-white rounded-3xl border transition-all duration-300 shadow-sm ${
                        expandedCourseId === course.courseId ? 'border-blue-100 ring-4 ring-blue-500/5 shadow-xl shadow-blue-900/5' : 'border-blue-50 hover:border-blue-200'
                    }`}>
                        <div className="p-4 md:p-5 flex flex-col md:flex-row justify-between items-center gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 shadow-inner">
                                    <BookOpen size={22} />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-slate-800 tracking-tight group-hover:text-blue-600 transition-colors">{course.courseName}</h2>
                                    <div className="flex items-center gap-3 mt-0.5">
                                        <span className="flex items-center gap-1.5 text-slate-400 text-xs font-bold uppercase tracking-widest">
                                            <Layers size={12} /> {course.quizzes?.length || 0} Assessments
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-center gap-2">
                                <button
                                    onClick={() => openAIAssistant(course)}
                                    className="px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all flex items-center gap-1.5 active:scale-95"
                                >
                                    <Brain size={14} /> AI Assistant
                                </button>
                                <button
                                    onClick={() => handleOpenImport(course.courseId, course.courseName)}
                                    className="px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-emerald-600 hover:text-white transition-all flex items-center gap-1.5 active:scale-95"
                                >
                                    <Upload size={14} /> Import
                                </button>
                                <button
                                    onClick={() => handleOpenExport(null, course)}
                                    className="px-4 py-2 bg-violet-50 text-violet-600 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-violet-600 hover:text-white transition-all flex items-center gap-1.5 active:scale-95"
                                    title="Export all quizzes for this course"
                                >
                                    <Download size={14} /> Export All
                                </button>
                                <button
                                    onClick={() => openCreateQuizModal(course.courseId, course.courseName)}
                                    className="px-4 py-2 bg-[#264b82] text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:shadow-lg shadow-blue-900/20 transition-all flex items-center gap-1.5 active:scale-95"
                                >
                                    <PlusCircle size={14} /> New Quiz
                                </button>
                                <div className="h-8 w-px bg-slate-100 mx-0.5 hidden md:block"></div>
                                <button
                                    onClick={() => toggleCourseExpansion(course.courseId)}
                                    className={`p-2 rounded-xl transition-all ${
                                        expandedCourseId === course.courseId ? 'bg-slate-50 text-slate-400 hover:bg-slate-100' : 'bg-blue-600 text-white shadow-lg shadow-blue-900/20 rotate-180'
                                    }`}
                                >
                                    <ChevronDown size={18} />
                                </button>
                            </div>
                        </div>

                        {expandedCourseId === course.courseId && (
                            <div className="px-5 pb-5 animate-in slide-in-from-top-4 duration-500">
                                <div className="border-t border-slate-50 pt-4 space-y-3">
                                    {course.quizzes?.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                            {course.quizzes.map((quiz) => (
                                                <div key={quiz.quizId} className="bg-slate-50/50 p-4 rounded-2xl border border-slate-100 hover:border-blue-200 hover:bg-white transition-all group relative overflow-hidden">
                                                    <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-full -mr-12 -mt-12 opacity-0 group-hover:opacity-100 transition-opacity blur-2xl"></div>

                                                    <div className="flex justify-between items-start mb-3 relative z-10">
                                                        <div className="bg-white p-2 rounded-xl shadow-sm text-blue-600">
                                                            <Target size={20} />
                                                        </div>
                                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button onClick={() => setViewingQuiz(quiz)} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg" title="View"><Eye size={14} /></button>
                                                            <button onClick={() => openEditQuizModal(quiz)} className="p-1.5 hover:bg-amber-50 text-amber-600 rounded-lg" title="Edit"><Edit size={14} /></button>
                                                            <button onClick={() => handleOpenExport(quiz, null)} className="p-1.5 hover:bg-violet-50 text-violet-600 rounded-lg" title="Export quiz"><Download size={14} /></button>
                                                            <button onClick={() => handleDeleteQuiz(quiz.quizId)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-lg" title="Delete"><Trash2 size={14} /></button>
                                                        </div>
                                                    </div>

                                                    <h4 className="font-bold text-slate-800 text-base mb-1.5 relative z-10">{quiz.quizName}</h4>
                                                    <div className="flex items-center gap-3 text-xs font-bold text-slate-400 relative z-10">
                                                        <span className="flex items-center gap-1"><Hash size={11} /> {quiz.questions?.length} Qs</span>
                                                        <span className="flex items-center gap-1 uppercase tracking-widest text-emerald-500"><CheckCircle2 size={11} /> Active</span>
                                                    </div>

                                                    <button
                                                        onClick={() => setViewingQuiz(quiz)}
                                                        className="mt-3 w-full py-2 bg-white border border-slate-200 rounded-xl font-bold text-[10px] uppercase tracking-widest text-slate-400 hover:border-blue-500 hover:text-blue-500 transition-all flex items-center justify-center gap-2"
                                                    >
                                                        Review Session <ArrowRight size={12} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <div className="bg-slate-50 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3">
                                                <AlertCircle size={24} className="text-slate-200" />
                                            </div>
                                            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">No assessments deployed</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        )}

        {/* Pagination */}
        {filteredCourses.length > examsPerPage && (
            <div className="mt-6 flex justify-center gap-2">
                <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 disabled:opacity-30 hover:bg-slate-50 transition-all shadow-sm"
                >
                    <ChevronLeft size={16} />
                </button>
                {[...Array(Math.ceil(filteredCourses.length / examsPerPage))].map((_, i) => (
                    <button
                        key={i}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`w-9 h-9 rounded-xl font-bold text-sm transition-all ${
                            currentPage === i + 1 ? 'bg-[#264b82] text-white shadow-lg shadow-blue-900/20' : 'bg-white text-slate-400 border border-slate-200 hover:bg-slate-50 shadow-sm'
                        }`}
                    >
                        {i + 1}
                    </button>
                ))}
                <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredCourses.length / examsPerPage)))}
                    disabled={currentPage === Math.ceil(filteredCourses.length / examsPerPage)}
                    className="p-2 bg-white border border-slate-200 rounded-xl text-slate-400 disabled:opacity-30 hover:bg-slate-50 transition-all shadow-sm"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        )}

        {/* Modals */}
        <QuizModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          courseId={selectedCourseId}
          courseName={selectedCourseName}
          existingQuiz={selectedQuiz}
          onQuizSaved={fetchCoursesWithQuizzes}
          aiGeneratedQuestions={aiGeneratedQuestions}
        />
        <AIAssistantModal
          isOpen={isAIModalOpen}
          onClose={() => setIsAIModalOpen(false)}
          courseId={selectedCourseId}
          courseName={selectedCourseName}
          onQuestionsGenerated={handleAIQuestionsGenerated}
        />
        <QuizDetails
          quiz={viewingQuiz}
          onClose={() => setViewingQuiz(null)}
        />
        <ImportModal
          isOpen={isImportModalOpen}
          onClose={() => setIsImportModalOpen(false)}
          courseId={importingCourse.id}
          courseName={importingCourse.name}
          onImportSuccess={fetchCoursesWithQuizzes}
        />
        <ExportModal
          isOpen={isExportModalOpen}
          onClose={() => setIsExportModalOpen(false)}
          quiz={exportTarget.quiz}
          course={exportTarget.course}
        />
      </div>
    </div>
  );
};

export default Quizs;